/**
 * Messages in parts, on a map's socket (docs/plans/2026-10-16-memory-per-
 * connection.md). A socket's buffers on the server grow to the largest
 * message they carried and keep that size while connected: as one message,
 * the opening sync or a snapshot (a whole map, up to 8 MB) made each socket
 * hold its map's size. In parts of at most PART_MAX, no more than that.
 *
 * A part: `{"type":"part","seq":i,"total":n,"data":"…"}`, back to back from
 * 0 to n - 1; the data put end to end is the message. The client asks for
 * parts in `sync-step1` (`parts: true`); the server says in `sync-step2` that
 * it reads them (`parts: true`). Either side without it: whole messages.
 * Server: `orbital_backend/src/handlers/sync/common.rs` (same limits).
 */

/** Most characters of a message in one part. */
export const PART_MAX = 16 * 1024;
const PARTS_MAX_CHARS = 8 * 1024 * 1024;
const PARTS_MAX_COUNT = 1024;

/**
 * A message's text as the parts to send, each a JSON string. Not cut inside
 * a character (a UTF-16 surrogate pair).
 * @param {string} text
 * @param {number} [max]
 * @returns {string[]}
 */
export function toParts(text, max = PART_MAX) {
  const pieces = [];
  let start = 0;
  while (text.length - start > max) {
    let end = start + max;
    const code = text.charCodeAt(end - 1);
    if (code >= 0xd800 && code <= 0xdbff) end -= 1; // a high surrogate: keep the pair whole
    pieces.push(text.slice(start, end));
    start = end;
  }
  pieces.push(text.slice(start));
  return pieces.map((data, seq) => JSON.stringify({ type: "part", seq, total: pieces.length, data }));
}

/**
 * Gathers the parts received on one socket (a new one for each socket).
 * `take(raw)` returns `{ whole }` (a message to handle: one sent whole, or
 * the last part's), `{ pending: true }` (a part kept), or `{ invalid }` (a
 * part out of order or too large: dropped, with what was gathered).
 */
export function createPartsReader() {
  let total = 0;
  let next = 0;
  let pieces = [];
  let size = 0;
  const reset = () => { total = 0; next = 0; pieces = []; size = 0; };

  function take(raw) {
    let part = null;
    // A part is small and says so; anything else (or what does not read as a
    // part) is a whole message, handled as before.
    if (typeof raw === "string" && raw.length <= 4 * PART_MAX + 256 && raw.includes('"type":"part"')) {
      try {
        const message = JSON.parse(raw);
        if (message?.type === "part" && Number.isInteger(message.seq) && Number.isInteger(message.total)
          && typeof message.data === "string") part = message;
      } catch {
        part = null;
      }
    }
    if (!part) {
      if (total) reset();
      return { whole: raw };
    }
    const fresh = total === 0;
    if ((fresh && (part.seq !== 0 || part.total < 2 || part.total > PARTS_MAX_COUNT))
      || (!fresh && (part.total !== total || part.seq !== next))
      || size + part.data.length > PARTS_MAX_CHARS) {
      reset();
      return { invalid: "Message parts out of order or too large" };
    }
    if (fresh) total = part.total;
    pieces.push(part.data);
    size += part.data.length;
    next = part.seq + 1;
    if (next < total) return { pending: true };
    const whole = pieces.join("");
    reset();
    return { whole };
  }

  return { take, reset };
}
